<template>
    <div class="node" :style="{ marginLeft: `${level * 20}px` }">
        <div
            class="node-header"
            @click="toggle"
            v-if="isCollapsible"
            :aria-expanded="isOpen"
            role="button"
            tabindex="0"
            @keydown.enter="toggle"
            @keydown.space.prevent="toggle"
        >
            <span class="toggle-icon">
                <span v-if="isOpen">▼</span>
                <span v-else>▶</span>
            </span>
            <span :class="typeClass">
                <strong>{{ node.name }}</strong>
                <span class="node-type badge text-bg-light">{{ mappedType }}</span>
                <span class="node-status" :class="statusClass">
                    <span v-if="!isPendingStatus">{{ statusMessage }}</span>
                    <template v-if="isLoadingStatus">
                        <span class="animated-ellipsis">
                            <span>.</span><span>.</span><span>.</span>
                        </span>
                    </template>
                </span>
            </span>
        </div>
        <div class="node-header non-collapsible" v-else>
            <span :class="typeClass">
                <strong>{{ node.name }}</strong>
                <span class="node-type badge text-bg-light">{{ mappedType }}</span>
                <span class="node-status" :class="statusClass">
                    <span v-if="!isPendingStatus">{{ statusMessage }}</span>
                    <template v-if="isLoadingStatus">
                        <span class="animated-ellipsis">
                            <span>.</span><span>.</span><span>.</span>
                        </span>
                    </template>
                </span>
            </span>
        </div>
        <div v-if="node.description" class="node-description">{{ node.description }}</div>
        <div v-show="isOpen" class="node-children" v-if="hasChildren">
            <CourseNode
                v-for="(child, index) in node.children"
                :key="index"
                :node="child"
                :level="level + 1"
                :collapsible="collapsible"
            />
        </div>
    </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop } from '@/helpers/Decorators';

interface CourseNodeType {
    type: string;
    name: string;
    description: string;
    status?: string;
    children?: CourseNodeType[];
}

@Options({
    name: 'CourseNode'
})

export default class CourseNode extends Vue {
    @Prop({ type: Object, required: true })
    public node!: CourseNodeType;

    @Prop({ type: Number, required: true })
    public level!: number;

    @Prop({ type: Boolean, default: false })
    public collapsible!: boolean;

    public isOpen: boolean = false;

    get hasChildren(): boolean {
        return this.node.children && this.node.children.length > 0;
    }

    get isCollapsible(): boolean {
        return this.collapsible && this.hasChildren;
    }

    get typeClass(): string {
        switch (this.node.type) {
            case 'el_course':
                return 'type-course';
            case 'el_topic':
                return 'type-topic';
            case 'el_lessons':
                return 'type-lesson';
            case 'Content':
                return 'type-content';
            case 'el_tests':
                return 'type-test';
            case 'SingleSelect':
                return 'type-single-select';
            case 'MultiSelect':
                return 'type-multi-select';
            default:
                return 'type-default';
        }
    }

    get mappedType(): string {
        const typeMap: Record<string, string> = {
            'el_course': 'Szkolenie',
            'el_topic': 'Temat',
            'el_tests': 'Test',
            'el_lessons': 'Lekcja',
            'Content': 'Materiał opisowy',
            'SingleSelect': 'Jednokrotnego wyboru',
            'MultiSelect': 'Wielokrotnego wyboru'
        };

        return typeMap[this.node.type] || this.node.type;
    }

    get statusMessage(): string {
        const statusMap: Record<string, string> = {
            'pending': 'oczekiwanie',
            'processing': 'przetwarzanie',
            'finished': 'zakończono',
            'failed': 'generowanie materiału nie powiodło się'
        };

        return this.node.status ? statusMap[this.node.status] : '';
    }

    get isLoadingStatus(): boolean {
        return this.node.status === 'processing';
    }

    get isPendingStatus(): boolean {
        return this.node.status === 'pending';
    }

    get statusClass(): string {
        switch (this.node.status) {
            case 'finished':
                return 'status-finished';
            case 'failed':
                return 'status-failed';
            default:
                return '';
        }
    }

    public toggle(): void {
        if (this.isCollapsible) {
            this.isOpen = !this.isOpen;
        }
    }
}
</script>

<style scoped>
.node {
    margin: 10px 0;
    -webkit-user-select: none; /* Safari */
    -ms-user-select: none; /* IE 10 and IE 11 */
    user-select: none; /* Standard syntax */
}

.node-header {
    display: flex;
    align-items: center;
    padding: 5px;
    border-radius: 4px;
    font-size: 1.1em;
}

.node-header:hover {
    background-color: #f0f0f0;
}

.toggle-icon {
    width: 20px;
    display: inline-block;
}

.node-header.non-collapsible {
    cursor: default;
}

.node-type {
    margin-left: 5px;
}

.node-status {
    margin-left: 5px;
    font-size: 0.7em;
    color: rgb(174, 174, 174);
}

.status-finished {
    color: green;
}

.status-failed {
    color: red;
}

.node-description {
    margin-left: 25px;
    font-size: 0.9em;
    color: #555;
}

.node-children {
    margin-top: 5px;
    border-left: 2px dashed #ccc;
    padding-left: 10px;
    transition: all 0.3s ease;
}

.type-course {
    color: #2c3e50;
}

.type-lesson {
    color: #76c5f7;
}

.type-topic {
    color: #63b6ac;
}

.type-content {
    color: #76c5f780;
}

.type-test {
    color: #8c97d3;
}

.type-single-select,
.type-multi-select {
    color: #8c97d480;
}

.type-default {
    color: #7f8c8d;
}

.animated-ellipsis {
    font-size: 1.6em;
    display: inline-flex;
    gap: 2px;
}

.animated-ellipsis span {
    animation: blink 1s infinite;
    opacity: 0;
}

.animated-ellipsis span:nth-child(1) {
    animation-delay: 0s;
}

.animated-ellipsis span:nth-child(2) {
    animation-delay: 0.2s;
}

.animated-ellipsis span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes blink {
    0% {
        opacity: 0;
    }
    50% {
        opacity: 1;
    }
    100% {
        opacity: 0;
    }
}
</style>
